import axios from "axios";
import firebase from "firebase/compat/app";
import { getToken } from "firebase/app-check";

const isLocalDev =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

function resolveApiBaseUrl() {
  const fromEnv = process.env.VUE_APP_API_BASE_URL;
  const projectId = process.env.VUE_APP_FIREBASE_PROJECT_ID;
  const productionUrl =
    projectId &&
    `https://us-central1-${projectId}.cloudfunctions.net/consultasV2/api/v1`;

  const pointsToEmulator =
    fromEnv && /127\.0\.0\.1|localhost:5001/.test(fromEnv);

  // Build con .env.local del emulador: en producción el navegador no puede usar 127.0.0.1
  if (!isLocalDev && pointsToEmulator && productionUrl) {
    console.warn(
      "[API] VUE_APP_API_BASE_URL apunta al emulador; usando Cloud Functions:",
      productionUrl,
    );
    return productionUrl;
  }

  return fromEnv || productionUrl;
}

const BASE_URL = resolveApiBaseUrl();
const isLocalhost = isLocalDev;

const API = axios.create({
  baseURL: BASE_URL,
});

let appCheckInstance = null;

export const setAppCheckInstance = (instance) => {
  appCheckInstance = instance;
};

let authReadyPromise = null;

const waitForAuth = async () => {
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
  await authReadyPromise;
  return firebase.auth().currentUser;
};

API.interceptors.request.use(
  async (config) => {
    try {
      const user = await waitForAuth();

      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      }

      if (!isLocalhost && appCheckInstance) {
        try {
          const appCheckTokenResult = await getToken(appCheckInstance, false);
          config.headers["X-Firebase-AppCheck"] = appCheckTokenResult.token;
        } catch (appCheckError) {
          console.warn("App Check token error:", appCheckError);
        }
      }
    } catch (error) {
      console.error("Error setting auth headers:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = firebase.auth().currentUser;

        if (user) {
          const newToken = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          return API(originalRequest);
        } else {
          console.error("User not found");
          window.location.href = "/login";
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        await firebase.auth().signOut();
        console.error("Token refresh failed");
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default API;
